import { Slot as SlotPrimitive } from 'radix-ui';
import React, { useImperativeHandle, useRef, useState } from 'react';

import { Button, Icon, SimpleTooltip, tv } from '@eluve/components';

const Slot = SlotPrimitive.Slot;

const contentEditableVariant = tv({
  base: '-mb-0.5 block border-b-2 border-transparent focus:border-bg-accent-strong-primary focus:outline-hidden',
});

interface ContentEditableProps extends React.HTMLAttributes<HTMLElement> {
  editButtonLabel?: string;
  onContentChange?: (content: string) => void;
}

export const ContentEditable = React.forwardRef<
  HTMLElement,
  ContentEditableProps
>(
  (
    { className, editButtonLabel, onContentChange, children, ...props },
    ref,
  ) => {
    const currentContent = useRef<string | null>(null);
    const [editing, setEditing] = useState(false);

    const containerRef = useRef<HTMLElement>(null);

    useImperativeHandle(ref, () => containerRef.current as HTMLElement);

    return (
      <div className="group w-full">
        <Slot
          {...props}
          className={contentEditableVariant({ className })}
          ref={containerRef}
          contentEditable
          onFocus={(e) => {
            currentContent.current = e.currentTarget.textContent;
            setEditing(true);
            if (containerRef.current) {
              window.getSelection()?.selectAllChildren(containerRef.current);
            }
            window.getSelection()?.collapseToEnd();
          }}
          onBlur={(e) => {
            setEditing(false);
            const newContent = e.currentTarget.textContent;

            if (newContent === currentContent.current) {
              return;
            }

            if (!newContent) {
              e.currentTarget.textContent = currentContent.current ?? '';
              return;
            }

            onContentChange?.(newContent);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          suppressContentEditableWarning
        >
          {children}
        </Slot>
        {!editing && (
          <SimpleTooltip label={editButtonLabel ?? 'Edit'}>
            <div className="-my-1 ml-1 inline-block h-12">
              <Button
                variant="ghost"
                size="lg"
                onClick={() => {
                  containerRef.current?.focus();
                }}
                aria-label={editButtonLabel ?? 'Edit'}
              >
                <Icon name="Pencil" size="auto" />
              </Button>
            </div>
          </SimpleTooltip>
        )}
      </div>
    );
  },
);
